import React, { useState, useEffect } from 'react';
import StandsGrid from './components/StandsGrid';
import ReservationForm from './components/ReservationForm';
import SelectedStands from './components/SelectedStands';
import { FormData, SelectedStand, Stand } from './types/stand.types';
import { setupWordPressIntegration, mapFormDataToWordPress, submitFormToWordPress, notifyHeightChange } from './wordpress-integration';
import { processSecureHubSpotSubmission } from './services/secureHubspotService';
import { standsData } from './data/standsData';


const App: React.FC = () => {
  const [selectedStands, setSelectedStands] = useState<string[]>([]);
  const [selectedStandsData, setSelectedStandsData] = useState<SelectedStand[]>([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);

  // ========================================
  // CONFIGURACIÓN DE INTEGRACIÓN CON WORDPRESS
  // ========================================

  // Configurar integración con WordPress
  useEffect(() => {
    const cleanup = setupWordPressIntegration();
    
    return cleanup;
  }, []);

  // Notificar cambios de altura cuando cambien los stands seleccionados
  useEffect(() => {
    // Notificar siempre que cambien los stands, con un pequeño delay
    const timeoutId = setTimeout(() => {
      notifyHeightChange();
    }, 150);
    
    return () => clearTimeout(timeoutId);
  }, [selectedStands.length, selectedStandsData.length]); // Depender del número de stands y datos

  // Notificar cambios de altura cuando cambie el formulario
  useEffect(() => {
    // Observer para detectar cambios en el formulario
    const formObserver = new MutationObserver(() => {
      setTimeout(() => {
        notifyHeightChange();
      }, 100);
    });

    const formSection = document.querySelector('.form-section');
    if (formSection) {
      formObserver.observe(formSection, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    return () => {
      formObserver.disconnect();
    };
  }, []);

  // Función para obtener el nombre descriptivo del stand
  const getStandDisplayName = (standId: string): string => {
    // Buscar en standsData primero
    const standData = standsData.find(s => s.id === standId);
    if (standData) {
      return standData.name;
    }
    
    // Fallback para casos especiales
    switch (standId) {
      case 'plaza-auto1':
        return 'C1';
      case 'plaza-auto2':
        return 'C11';
      case 'plaza-innovacion1':
        return 'C21';
      case 'exA':
        return 'STANDS EXTERIORES A';
      case 'exB':
        return 'STANDS EXTERIORES B';
      case 'exC':
        return 'STANDS EXTERIORES C';
      default:
        return `Stand ${standId}`;
    }
  };

  // Función para obtener el tamaño real del stand
  const getStandSize = (standId: string): string => {
    // Buscar en standsData primero
    const standData = standsData.find(s => s.id === standId);
    if (standData && standData.size) {
      return standData.size;
    }
    
    // Fallback para casos especiales
    switch (standId) {
      // Plazas automotrices - 40m²
      case 'plaza-auto1': case 'plaza-auto2':
        return '40m²';
      
      // Plaza de la innovación - 38.5m²
      case 'plaza-innovacion1':
        return '38.5m²';
      
      // Stands exteriores - sin medida específica
      case 'exA': case 'exB': case 'exC':
        return '';
      
      default:
        return '5m²'; // Tamaño por defecto
    }
  };

  const handleStandToggle = (standId: string) => {
    setSelectedStands(prev => {
      if (prev.includes(standId)) {
        // Remover stand
        return prev.filter(id => id !== standId);
      } else {
        // Agregar stand
        return [...prev, standId];
      }
    });

    setSelectedStandsData(prevData => {
      if (prevData.some(s => s.id === standId)) {
        // Remover stand
        return prevData.filter(s => s.id !== standId);
      } else {
        // Agregar stand - crear un objeto básico
        return [
          ...prevData,
          {
            id: standId,
            name: getStandDisplayName(standId),
            price: undefined,
            size: getStandSize(standId)
          }
        ];
      }
    });

    // Notificar cambio de altura después de actualizar el estado
    setTimeout(() => {
      notifyHeightChange();
    }, 100);
  };

  const handleRemoveStand = (standId: string) => {
    setSelectedStands(prev => prev.filter(id => id !== standId));
    setSelectedStandsData(prev => prev.filter(s => s.id !== standId));
    
    // Notificar cambio de altura después de remover stand
    setTimeout(() => {
      notifyHeightChange();
    }, 100);
  };

  const handleFormSubmit = async (data: FormData, action: 'replace' | 'add' | 'new' = 'new', previousQueryId?: string) => {
    try {
      // Mapear datos al formato de WordPress y enviar (esto es lo más importante)
      const wordpressData = mapFormDataToWordPress(data, selectedStandsData);
      submitFormToWordPress(wordpressData, action, previousQueryId);
      
      // Mostrar mensaje de éxito inmediatamente
      setShowSuccessMessage(true);
      
      // Limpiar mensaje de éxito después de 5 segundos
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      
      // Enviar a HubSpot en segundo plano (sin bloquear la UI)
      const standsForHubSpot = convertToStands(selectedStandsData);
      sendToHubSpot(data, standsForHubSpot, action, previousQueryId).catch(error => {
        console.error('Error en HubSpot (no crítico):', error);
      });
      
    } catch (error) {
      console.error('Error enviando formulario:', error);
      // Aún así mostrar mensaje de éxito para no confundir al usuario
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    }
  };

  // Función para enviar datos a HubSpot (proceso completo: Contacto + Empresa + Deal)
  const sendToHubSpot = async (data: FormData, stands: Stand[], action: 'replace' | 'add' | 'new' = 'new', previousQueryId?: string) => {
    try {
      console.log('🚀 Iniciando proceso completo de HubSpot...');
      
      const result = await processSecureHubSpotSubmission(data, stands);
      
      if (result.success) {
        console.log('✅ Proceso completo de HubSpot exitoso:');
        console.log(`📞 Contacto: ${result.contactId}`);
        console.log(`🏢 Empresa: ${result.companyId}`);
        console.log(`💰 Deal: ${result.dealId}`);
      } else {
        console.error('❌ Error en proceso de HubSpot:', result.error);
      }
    } catch (error) {
      console.error('❌ Error inesperado en HubSpot:', error);
      // No lanzar el error para no interrumpir el flujo principal
    }
  };

  const handleFormReset = () => {
    // Limpiar stands seleccionados
    setSelectedStands([]);
    setSelectedStandsData([]);
  };

  // Función para verificar emails duplicados
  const checkDuplicateEmail = async (email: string) => {
    // Desactivar detección de emails duplicados temporalmente
    console.log('🚫 Detección de emails duplicados desactivada');
    return { isDuplicate: false };
    
    // Código original comentado para reactivar cuando sea necesario
    /*
    try {
      // Aquí harías la llamada a tu API/WordPress para verificar si el email ya existe
      // Por ahora, simulamos la respuesta
      const response = await fetch('/wp-json/stand-reservation/v1/check-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        // Si hay error en la API, asumir que no hay duplicado
        return { isDuplicate: false };
      }
    } catch (error) {
      console.error('Error checking duplicate email:', error);
      // En caso de error, asumir que no hay duplicado para no bloquear al usuario
      return { isDuplicate: false };
    }
    */
  };

  // Función para convertir SelectedStand[] a Stand[]
  const convertToStands = (selectedStands: SelectedStand[]): Stand[] => {
    return selectedStands.map(stand => ({
      id: stand.id,
      name: stand.name,
      category: 'custom' as const,
      price: stand.price,
      size: stand.size,
      available: true,
      isSelectable: true,
      className: `stand-${stand.id}`
    }));
  };

  return (
    <div className="app">
      {/* Mapa de Stands */}
      <div className="stands-section">
        <StandsGrid
          selectedStands={selectedStands}
          onStandToggle={handleStandToggle}
        />
      </div>
      
      {/* Leyenda con padding externo */}
      <div className="stands-legend-container">
        <div className="stands-legend">
          <span className="legend-item">
            <span className="legend-color" style={{background:'#F8981D'}}></span>
            STANDS 5m²
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{background:'#26A69A'}}></span>
            STANDS 10m²
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{background:'#1E88E5'}}></span>
            STANDS 20m²
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{background:'#139B47B3'}}></span>
            STANDS 40m²
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{background:'#0D7A3D'}}></span>
            STANDS 38.5m²
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{background:'#4BE8F9'}}></span>
            STANDS 40m²
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{background:'#2BC4D9'}}></span>
            STANDS 60m²
          </span>
        </div>
      </div>

      {/* Formulario y Stands Seleccionados */}
      <div className="form-section">
        <div className="stands-column">
          <SelectedStands
            selectedStands={selectedStandsData}
            onRemoveStand={handleRemoveStand}
          />
        </div>
        <div className="form-column">
          <ReservationForm 
            onSubmit={handleFormSubmit} 
            selectedStandsCount={selectedStands.length}
            onReset={handleFormReset}
            showSuccessMessage={showSuccessMessage}
            selectedStands={convertToStands(selectedStandsData)}
            onCheckDuplicateEmail={checkDuplicateEmail}
          />
        </div>
      </div>
    </div>
  );
};

export default App;